import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactElement } from "react";
import { ContentHeader, Modal, QueryStatus, ScaledInput, StatusBadge, useToast } from "@cx/ui";
import type {
  FinancialAccountView,
  NfceCscView,
  PosSettingsView,
  PosTerminalInput,
  PosTerminalView,
  WarehouseView,
} from "@cx/contracts";
import { formatPct, toPct4 } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";

const pctNumber = (pct4: number) => formatPct(pct4).replace("%", "");

/**
 * Vendas › PDV › Configuração (T3.1): limite de desconto, etiqueta de balança, NFC-e e contingência; terminais (conta
 * caixa, depósito, série da NFC-e, modo contingência); CSC da NFC-e (segredo fiscal, gravado cifrado).
 */
export function PosSettingsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canManage = can(perms, "pos", "update");
  const canCsc = can(perms, "fiscal_certificates", "update");
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useQuery({
    queryKey: ["pos-settings", companyId],
    queryFn: () => api<PosSettingsView>("/pos/settings"),
  });
  const terminals = useQuery({
    queryKey: ["pos-terminals", companyId],
    queryFn: () => api<PosTerminalView[]>("/pos/terminals"),
  });
  const [f, setF] = useState<PosSettingsView | null>(null);
  useEffect(() => {
    if (settings.data) setF(settings.data);
  }, [settings.data]);
  const save = useMutation({
    mutationFn: (v: PosSettingsView) =>
      api<PosSettingsView>("/pos/settings", {
        method: "PUT",
        body: JSON.stringify({
          defaultDiscountLimitPct4: v.defaultDiscountLimitPct4,
          roleDiscountLimits: v.roleDiscountLimits,
          scale: v.scale,
          issueNfce: v.issueNfce,
          contingencyHours: v.contingencyHours,
        }),
      }),
    onSuccess: (v) => {
      qc.setQueryData(["pos-settings", companyId], v);
      toast("success", "Configuração do PDV salva.");
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const [editing, setEditing] = useState<PosTerminalView | "new" | null>(null);

  return (
    <>
      <ContentHeader title="Configuração do PDV" breadcrumb={["Vendas", "PDV", "Configuração"]} />
      <QueryStatus loading={settings.isLoading} error={settings.error} />
      {f && (
        <form
          className="box card p-3 mb-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(f);
          }}
        >
          <h2 className="h6">Vendas e NFC-e</h2>
          <fieldset disabled={!canManage} className="row g-3">
            <div className="col-md-4">
              <label className="form-label" htmlFor="pos-discount">
                Desconto máximo sem regra do perfil
              </label>
              <ScaledInput
                id="pos-discount"
                value={f.defaultDiscountLimitPct4}
                parse={toPct4}
                format={pctNumber}
                suffix="%"
                onChange={(v) => setF({ ...f, defaultDiscountLimitPct4: v ?? 0 })}
              />
              <div className="form-text">
                Quem administra o PDV não tem limite. {f.roleDiscountLimits.length} perfil(is) com limite
                próprio.
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label" htmlFor="pos-cont-hours">
                Prazo da contingência (horas)
              </label>
              <input
                id="pos-cont-hours"
                type="number"
                min={1}
                max={168}
                className="form-control"
                value={f.contingencyHours}
                onChange={(e) => setF({ ...f, contingencyHours: Number(e.target.value) || 1 })}
              />
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <div className="form-check form-switch">
                <input
                  id="pos-issue-nfce"
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={f.issueNfce}
                  onChange={(e) => setF({ ...f, issueNfce: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="pos-issue-nfce">
                  Emitir NFC-e em toda venda
                </label>
              </div>
            </div>
            <h3 className="h6 col-12 mb-0">Etiqueta de balança (EAN-13)</h3>
            <div className="col-md-4">
              <label className="form-label" htmlFor="pos-scale-prefix">
                Prefixo
              </label>
              <select
                id="pos-scale-prefix"
                className="form-select"
                value={f.scale.prefix}
                onChange={(e) => setF({ ...f, scale: { ...f.scale, prefix: e.target.value } })}
              >
                {["2", ...Array.from({ length: 10 }, (_, k) => `2${k}`)].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label" htmlFor="pos-scale-len">
                Dígitos do código do produto
              </label>
              <select
                id="pos-scale-len"
                className="form-select"
                value={f.scale.codeLength}
                onChange={(e) => setF({ ...f, scale: { ...f.scale, codeLength: Number(e.target.value) } })}
              >
                {[4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label" htmlFor="pos-scale-kind">
                Valor embutido
              </label>
              <select
                id="pos-scale-kind"
                className="form-select"
                value={f.scale.valueKind}
                onChange={(e) =>
                  setF({ ...f, scale: { ...f.scale, valueKind: e.target.value as "price" | "weight" } })
                }
              >
                <option value="price">Preço (centavos)</option>
                <option value="weight">Peso (gramas)</option>
              </select>
              <div className="form-text">O código do produto na etiqueta é o SKU numérico do cadastro.</div>
            </div>
          </fieldset>
          {canManage && (
            <div className="mt-3">
              <button type="submit" className="btn btn-primary" disabled={save.isPending}>
                Salvar
              </button>
            </div>
          )}
        </form>
      )}

      <div className="box card mb-3">
        <div className="d-flex justify-content-between align-items-center p-3 pb-0">
          <h2 className="h6 mb-0">Terminais</h2>
          {canManage && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => setEditing("new")}
            >
              Novo terminal
            </button>
          )}
        </div>
        <QueryStatus loading={terminals.isLoading} error={terminals.error} />
        {terminals.data && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Terminais">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Conta caixa</th>
                  <th>Série NFC-e</th>
                  <th>Contingência</th>
                  <th>Caixa</th>
                  <th>
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {terminals.data.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-muted text-center py-3">
                      Nenhum terminal. Cadastre um para abrir o caixa no PDV.
                    </td>
                  </tr>
                )}
                {terminals.data.map((t) => (
                  <tr key={t.id}>
                    <td>
                      {t.name}
                      {!t.active && <span className="badge text-bg-secondary ms-2">Inativo</span>}
                    </td>
                    <td>{t.cashAccountName}</td>
                    <td>{t.nfceSeries}</td>
                    <td>{t.contingencyMode ? <StatusBadge tone="warning">Ligada</StatusBadge> : "—"}</td>
                    <td>{t.openSession ? `nº ${t.openSession.number} aberto` : "Fechado"}</td>
                    <td className="text-end">
                      {canManage && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setEditing(t)}
                        >
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <CscCard canEdit={canCsc} />
      {editing && (
        <TerminalDialog
          terminal={editing === "new" ? null : editing}
          onClose={(saved) => {
            setEditing(null);
            if (saved) void qc.invalidateQueries({ queryKey: ["pos-terminals", companyId] });
          }}
        />
      )}
    </>
  );
}

function TerminalDialog({
  terminal,
  onClose,
}: {
  terminal: PosTerminalView | null;
  onClose(saved: boolean): void;
}) {
  const { companyId, api } = useSession();
  const toast = useToast();
  const accounts = useQuery({
    queryKey: ["financial-accounts", companyId],
    queryFn: () => api<{ data: FinancialAccountView[] }>("/financial-accounts"),
  });
  const warehouses = useQuery({
    queryKey: ["warehouses", companyId],
    queryFn: () => api<{ data: WarehouseView[] }>("/warehouses"),
  });
  const [f, setF] = useState<PosTerminalInput>(
    terminal
      ? {
          name: terminal.name,
          cashAccountId: terminal.cashAccountId,
          warehouseId: terminal.warehouseId,
          priceListId: terminal.priceListId,
          nfceSeries: terminal.nfceSeries,
          contingencyMode: terminal.contingencyMode,
          active: terminal.active,
        }
      : {
          name: "",
          cashAccountId: "",
          warehouseId: null,
          priceListId: null,
          nfceSeries: 1,
          contingencyMode: false,
          active: true,
        },
  );
  const cashAccounts = (accounts.data?.data ?? []).filter((a) => a.kind === "cash");
  useEffect(() => {
    if (!f.cashAccountId && cashAccounts[0]) setF((x) => ({ ...x, cashAccountId: cashAccounts[0]!.id }));
  }, [cashAccounts, f.cashAccountId]);
  const m = useMutation({
    mutationFn: () =>
      api(terminal ? `/pos/terminals/${terminal.id}` : "/pos/terminals", {
        method: terminal ? "PUT" : "POST",
        body: JSON.stringify(f),
      }),
    onSuccess: () => {
      toast("success", "Terminal salvo.");
      onClose(true);
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <Modal
      open
      title={terminal ? "Editar terminal" : "Novo terminal"}
      onClose={() => onClose(false)}
      footer={
        <button
          type="button"
          className="btn btn-primary"
          disabled={m.isPending || !f.name.trim() || !f.cashAccountId}
          onClick={() => m.mutate()}
        >
          Salvar
        </button>
      }
    >
      <label className="form-label" htmlFor="term-name">
        Nome
      </label>
      <input
        id="term-name"
        className="form-control mb-2"
        maxLength={60}
        value={f.name}
        onChange={(e) => setF({ ...f, name: e.target.value })}
        data-autofocus
      />
      <label className="form-label" htmlFor="term-account">
        Conta caixa (dinheiro do terminal)
      </label>
      <select
        id="term-account"
        className="form-select mb-2"
        value={f.cashAccountId}
        onChange={(e) => setF({ ...f, cashAccountId: e.target.value })}
      >
        {cashAccounts.length === 0 && <option value="">Cadastre uma conta do tipo caixa em Finanças</option>}
        {cashAccounts.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>
      <label className="form-label" htmlFor="term-wh">
        Depósito
      </label>
      <select
        id="term-wh"
        className="form-select mb-2"
        value={f.warehouseId ?? ""}
        onChange={(e) => setF({ ...f, warehouseId: e.target.value || null })}
      >
        <option value="">Depósito padrão</option>
        {(warehouses.data?.data ?? []).map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
      </select>
      <label className="form-label" htmlFor="term-series">
        Série da NFC-e
      </label>
      <input
        id="term-series"
        type="number"
        min={0}
        max={889}
        className="form-control mb-2"
        value={f.nfceSeries}
        onChange={(e) => setF({ ...f, nfceSeries: Number(e.target.value) })}
      />
      <div className="form-check form-switch">
        <input
          id="term-cont"
          className="form-check-input"
          type="checkbox"
          role="switch"
          checked={f.contingencyMode}
          onChange={(e) => setF({ ...f, contingencyMode: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="term-cont">
          Modo contingência (SEFAZ fora do ar): NFC-e com tpEmis 9
        </label>
      </div>
      <div className="form-check form-switch">
        <input
          id="term-active"
          className="form-check-input"
          type="checkbox"
          role="switch"
          checked={f.active}
          onChange={(e) => setF({ ...f, active: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="term-active">
          Ativo
        </label>
      </div>
    </Modal>
  );
}

function CscCard({ canEdit }: { canEdit: boolean }) {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ["nfce-csc", companyId],
    queryFn: () => api<NfceCscView>("/pos/nfce-csc"),
    retry: false,
  });
  const [cscId, setCscId] = useState("");
  const [csc, setCsc] = useState("");
  const m = useMutation({
    mutationFn: () =>
      api<NfceCscView>("/pos/nfce-csc", { method: "PUT", body: JSON.stringify({ cscId, csc }) }),
    onSuccess: (v) => {
      qc.setQueryData(["nfce-csc", companyId], v);
      setCsc("");
      toast("success", "CSC da NFC-e salvo.");
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <div className="box card p-3">
      <h2 className="h6">CSC da NFC-e</h2>
      <p className="small text-muted">
        Código de Segurança do Contribuinte, gerado no portal da SEFAZ da sua UF. Fica cifrado: só os 4
        últimos caracteres aparecem aqui.
      </p>
      {q.data && (
        <p data-testid="nfce-csc-status">
          {q.data.configured ? (
            <>
              <StatusBadge tone="success">Cadastrado</StatusBadge> Id {q.data.cscId} · final {q.data.last4}
            </>
          ) : (
            <StatusBadge tone="warning">Não cadastrado</StatusBadge>
          )}
        </p>
      )}
      {canEdit && (
        <form
          className="row g-2 align-items-end"
          onSubmit={(e) => {
            e.preventDefault();
            m.mutate();
          }}
        >
          <div className="col-md-3">
            <label className="form-label" htmlFor="csc-id">
              Id do CSC
            </label>
            <input
              id="csc-id"
              className="form-control"
              inputMode="numeric"
              value={cscId}
              onChange={(e) => setCscId(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label" htmlFor="csc-value">
              CSC
            </label>
            <input
              id="csc-value"
              className="form-control"
              autoComplete="off"
              value={csc}
              onChange={(e) => setCsc(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <button type="submit" className="btn btn-primary" disabled={m.isPending || !cscId || !csc}>
              Salvar CSC
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
